import {
  MouseEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type VaultMediaFilesDto, VaultMediaType } from '@api'
import { useUnit } from 'effector-react/effector-react.mjs'
import { createPortal } from 'react-dom'
import { twMerge } from 'tailwind-merge'

import { ExclamationIcon } from '@/assets/icons/icon-exclamation'
import { IconLoading } from '@/assets/icons/icon-loading'
import PlayIcon from '@/assets/icons/icon-play.svg'
import IconPriceTag from '@/assets/icons/icon-price-tag.svg'
import { MediaThumb } from '@/components/ui/MediaThumb'
import { $mediaFailedAssets } from '@/models/vault/medias/errors.ts'
import { isStreamable } from '@/modules/vault/utils.ts'
import { classNames } from '@/utils/helpers'
import { noop } from '@/utils/misc.ts'

import { LogoPlaceholder } from './logo-placeholder'

interface Props {
  files: VaultMediaFilesDto
  type: VaultMediaType
  fit?: 'contain' | 'cover'
  isReady?: boolean
  hasError?: boolean
  isFree?: boolean
  className?: string
  title?: string
  isLoading?: boolean

  onPlayClick?: MouseEventHandler<HTMLOrSVGElement>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export const VaultMediaPreview = memo(
  ({
    files,
    fit = 'cover',
    type,
    isReady = true,
    isFree,
    hasError,
    className,
    title,
    onPlayClick,
    isLoading,
    onMouseEnter = noop,
    onMouseLeave = noop,
  }: Props) => {
    const mediaFailedAssets = useUnit($mediaFailedAssets)
    const [showTooltip, setShowTooltip] = useState(false)
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
    const [isHovered, setIsHovered] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const handleMouseEnter = useCallback(
      (event: React.MouseEvent) => {
        setIsHovered(true)
        if (title) {
          const rect = event.currentTarget.getBoundingClientRect()

          setTooltipPosition({
            x: rect.left + 8,
            y: rect.top + 8,
          })
          setShowTooltip(true)
        } else {
          onMouseEnter()
        }
      },
      [title, onMouseEnter],
    )

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false)
      setShowTooltip(false)
      onMouseLeave()
    }, [onMouseLeave])

    useEffect(() => {
      if (isHovered && title && !showTooltip && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()

        setTooltipPosition({
          x: rect.left + 8,
          y: rect.top + 8,
        })
        setShowTooltip(true)
      }
    }, [title, isHovered, showTooltip])

    const shouldShowThumb =
      [VaultMediaType.PHOTO, VaultMediaType.VIDEO, VaultMediaType.GIF].includes(
        type,
      ) &&
      !!files.thumb?.url &&
      !mediaFailedAssets[files.thumb.url]

    const thumbUrl = shouldShowThumb ? files.thumb?.url : undefined

    return (
      <div
        ref={containerRef}
        className={classNames(
          'flex flex-col h-full w-full relative',
          className,
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={classNames(
            'relative left-0 top-0 flex h-full w-full justify-center overflow-hidden',
            {
              'cursor-pointer': isReady && !isLoading,
              'cursor-wait': isLoading,
            },
          )}
        >
          {hasError ? (
            <div className="relative flex min-h-[144px] min-w-[144px] flex-1 flex-col items-center justify-center gap-2 bg-gray-300 px-2 py-4 text-center dark:bg-gray-800">
              <ExclamationIcon className="ml-0 mr-0 h-6 w-6" />
              <span className="text-text-default text-xs">
                Media couldn&apos;t be processed. Please upload another media.
              </span>
            </div>
          ) : (
            <div className="relative w-full h-full">
              {thumbUrl ? (
                <MediaThumb
                  src={thumbUrl}
                  mediaType={type}
                  alt=""
                  className={twMerge(
                    'left-0 top-0 h-full w-full object-cover',
                    'transition-transform duration-200 hover:scale-105',
                    fit === 'contain' ? 'object-contain' : 'object-cover',
                  )}
                />
              ) : (
                <LogoPlaceholder showText={type !== VaultMediaType.AUDIO} />
              )}
              {isReady && isStreamable({ type, full: files?.full?.url }) && (
                <div
                  onClick={onPlayClick}
                  className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#00000099] p-3 duration-200"
                >
                  <PlayIcon className="h-4 w-4 text-white" />
                </div>
              )}
              {!isReady && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#00000099] px-2 py-4 text-center pointer-events-none">
                  <IconLoading className="ml-0 mr-0 h-4 w-4 animate-spin" />
                  <span className="text-text-default text-xs">
                    This media is currently being processed
                  </span>
                </div>
              )}
            </div>
          )}
          {isFree && (
            <div className="absolute right-0 top-0 m-1 p-1">
              <IconPriceTag className="h-5 w-5" />
            </div>
          )}
        </div>
        {showTooltip &&
          title &&
          createPortal(
            <div
              className="fixed bg-black/90 text-white text-xs px-3 py-1.5 rounded-md z-[9999] shadow-lg pointer-events-none max-w-[400px] break-words"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y,
              }}
            >
              {title}
            </div>,
            document.body,
          )}
      </div>
    )
  },
)

VaultMediaPreview.displayName = 'VaultMediaPreview'
